import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Modal } from 'react-bootstrap';
import { map, omit, pick } from 'lodash';
import { isBlank } from 'helpers/string_helpers';

import EditContactForm from 'components/contacts/edit_contact_form';
import { updateCompany } from 'components/contacts/companies/api';
import { updatePerson } from 'components/contacts/people/api';

const withPreventDefault = handlingFunction => (e) => {
  e.preventDefault();
  handlingFunction();
};

export default class EditContactModal extends React.Component {
  static propTypes = {
    lead: PropTypes.shape({
      accessibleDatabaseContacts: PropTypes.arrayOf(PropTypes.shape({
        databaseName: PropTypes.string
      })).isRequired,
      contactId: PropTypes.number.isRequired,
      contactType: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired
    }),
    shown: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
  };

  static defaultProps = {
    shown: false
  };

  state = {
    contact: null,
    errorMessage: null,
    saving: false,
    persistAddress: false
  };

  getModalProps = () => {
    omit(this.props, Object.keys(EditContactModal.propTypes));
  };

  handleSave = () => {
    const { lead, onSave } = this.props;
    const { contact, persistAddress } = this.state;
    let savePromise;

    if (!contact) {
      // eslint-disable-next-line no-console
      console.error('Cannot save null contact');
      return;
    }

    this.setState({ saving: true, errorMessage: null });

    if (lead.contactType === 'Contacts::Company') {
      savePromise = updateCompany(contact, persistAddress ? contact.address : null);
    } else {
      const contactFields = pick(
        contact,
        'id',
        'email',
        'firstName',
        'jobTitle',
        'lastName',
        'mobilePhoneNumber',
        'officePhoneNumber',
        'companyName'
      );
      savePromise = updatePerson(contactFields);
    }

    savePromise.then((contact) => {
      onSave(contact);
      this.setState({ contact, saving: false });
    }).catch((errors) => {
      this.setState({ saving: false, errorMessage: Object.values(errors)[0] });
    });
  }

  clearContactInfo = () => {
    this.setState({ contact: null });
  };

  updateContactInfo = (contact, persistAddress = false) => {
    this.setState({ contact, persistAddress });
  };

  onCancelModal = () => {
    this.setState({ errorMessage: null });
    this.props.onCancel();
  }

  render() {
    const { lead, shown } = this.props;
    const { contact, saving, errorMessage } = this.state;
    if (!lead || !lead.contactId || !lead.contactType) return null;

    return (
      <Modal name="edit-lead-contact-modal" show={shown} onHide={this.onCancelModal} {...this.getModalProps()}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Contact</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.renderMessage()}
          {this.renderAlert(errorMessage)}
          <EditContactForm
            hasCrm={lead.accessibleDatabaseContacts.length > 0}
            id={lead.contactId}
            type={lead.contactType}
            onChange={this.updateContactInfo}
            onLoad={this.updateContactInfo}
            onLoading={this.clearContactInfo}
          />
        </Modal.Body>
        <Modal.Footer className="text-right">
          <button
            className="btn btn-cancel"
            name="cancel-edit-contact-btn"
            type="button"
            onClick={withPreventDefault(this.onCancelModal)}
          >
            Cancel
          </button>
          <button
            className="btn btn-save"
            disabled={!contact || saving}
            name="save-contact-btn"
            type="button"
            onClick={withPreventDefault(this.handleSave)}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  renderMessage() {
    const { lead: { accessibleDatabaseContacts, displayName } } = this.props;
    if (accessibleDatabaseContacts.length === 0 || isBlank(displayName)) return null;

    if (accessibleDatabaseContacts.length === 1) {
      const databaseName = <strong>{accessibleDatabaseContacts[0].databaseName}</strong>;
      return (
        <p>
          Since <strong>{displayName}</strong> exists in {databaseName}, any information you change will also be changed
          for this Contact in {databaseName}.
        </p>
      );
    } else {
      const databaseNames = map(accessibleDatabaseContacts, contact => (
        <li key={`database-name-${contact.databaseId}`}><strong>{contact.databaseName}</strong></li>
      ));
      return (
        <React.Fragment>
          <p>
            Since <strong>{displayName}</strong> exists in {databaseNames.length} databases, any information you change
            will also be changed for this Contact in these databases:
          </p>
          <ul className="mt-1 mb-4">{databaseNames}</ul>
        </React.Fragment>
      );
    }
  }

  renderAlert(message) {
    return (
      message &&
      <Alert bsStyle="danger" className="mt-3">{message}</Alert>
    );
  }
}
